// Item registry: things that live in inventory slots. An inventory slot holds a
// unified item id — block ids (0-99, from blocks.ts) OR the tool/material ids
// below (>= 100). Mining speed and attack damage are data (this registry), not
// branching logic, so adding a tool is one entry.

import { Block, blockDef } from './blocks';

/** Non-block item ids. Block items reuse Block ids (all < 100). */
export const enum Item {
  Stick = 100,
  WoodPickaxe = 101,
  StonePickaxe = 102,
  WoodAxe = 103,
  StoneAxe = 104,
  WoodSword = 105,
  StoneSword = 106,
}

/** Which tool a block is broken fastest with. */
export type MineTool = 'pickaxe' | 'axe' | 'shovel' | 'none';

export interface ToolDef {
  id: Item;
  name: string;
  /** The kind of block this tool speeds up. */
  tool: Exclude<MineTool, 'none'>;
  /** Mining-speed multiplier applied to matching blocks (hand = 1). */
  speed: number;
  /** Melee damage when used as a weapon (hand = 1). */
  attackDamage: number;
  // Durability would live here (e.g. maxUses) — intentionally omitted for v1.
}

const TOOLS: Partial<Record<Item, ToolDef>> = {
  [Item.WoodPickaxe]: {
    id: Item.WoodPickaxe,
    name: 'Wooden Pickaxe',
    tool: 'pickaxe',
    speed: 4,
    attackDamage: 2,
  },
  [Item.StonePickaxe]: {
    id: Item.StonePickaxe,
    name: 'Stone Pickaxe',
    tool: 'pickaxe',
    speed: 6,
    attackDamage: 3,
  },
  [Item.WoodAxe]: { id: Item.WoodAxe, name: 'Wooden Axe', tool: 'axe', speed: 4, attackDamage: 3 },
  [Item.StoneAxe]: { id: Item.StoneAxe, name: 'Stone Axe', tool: 'axe', speed: 6, attackDamage: 4 },
  [Item.WoodSword]: {
    id: Item.WoodSword,
    name: 'Wooden Sword',
    tool: 'pickaxe',
    speed: 1,
    attackDamage: 4,
  },
  [Item.StoneSword]: {
    id: Item.StoneSword,
    name: 'Stone Sword',
    tool: 'pickaxe',
    speed: 1,
    attackDamage: 5,
  },
};

const NON_TOOL_NAMES: Partial<Record<Item, string>> = {
  [Item.Stick]: 'Stick',
};

/** True if an item id refers to a placeable block (vs a tool/material). */
export function isBlockItem(id: number): id is Block {
  return id < 100;
}

export function toolDef(id: number): ToolDef | null {
  return TOOLS[id as Item] ?? null;
}

export function itemName(id: number): string {
  if (isBlockItem(id)) return blockDef(id).name;
  return TOOLS[id as Item]?.name ?? NON_TOOL_NAMES[id as Item] ?? 'Item';
}

/** Tools stack to 1; blocks and materials stack to 64. */
export function maxStackOf(id: number): number {
  return toolDef(id) ? 1 : 64;
}

/** Which tool breaks a block fastest. Data table — no per-call branching cost. */
export function blockMineTool(block: Block): MineTool {
  switch (block) {
    case Block.Stone:
    case Block.Cobblestone:
      return 'pickaxe';
    case Block.Wood:
    case Block.Planks:
    case Block.CraftingTable:
      return 'axe';
    case Block.Dirt:
    case Block.Grass:
    case Block.Sand:
      return 'shovel';
    default:
      return 'none';
  }
}

/** Mining-speed multiplier for using `itemId` on `block` (hand/others = 1). */
export function miningMultiplier(itemId: number | null, block: Block): number {
  if (itemId === null) return 1;
  const tool = toolDef(itemId);
  if (!tool) return 1;
  return blockMineTool(block) === tool.tool ? tool.speed : 1;
}

/** Melee damage for the held item (hand or non-weapon = 1). */
export function attackDamageOf(itemId: number | null): number {
  if (itemId === null) return 1;
  return toolDef(itemId)?.attackDamage ?? 1;
}
